import type { AuthorParams } from './useAuthorQuery'
import type { Author } from '~/utils/solr/author'
import { SolrParamsKey } from '#shared/solr'
import { extractLabel, getLastName } from '#shared/utils/solr-facet'
import { compareLocaleNorwegian, normalizeNorwegianSort } from '@/utils/string'
import { createCountWithFilters } from '~/utils/filter-count'
import { useAuthorQuery } from './useAuthorQuery'

export const AUTHORS_PER_PAGE = 64

interface AuthorFilterState {
  searchKeyword: string
  selectedLegalArea: { value: string, label: string }[]
}

type AuthorDrawerOverrides = Omit<AuthorFilterState, 'searchKeyword'>

export function authorMatches(author: Author, state: AuthorFilterState): boolean {
  if (!matchesAllSearchTerms(state.searchKeyword, [author.name])) {
    return false
  }

  if (state.selectedLegalArea.length > 0) {
    const authorAreas = new Set(author.legalAreas)
    if (!state.selectedLegalArea.some(area => authorAreas.has(area.value))) {
      return false
    }
  }
  return true
}

export function _useAuthorFilters() {
  const page = useState<number>('author-filter-page', () => 1)
  const searchKeyword = useState<string>('author-filter-search', () => '')
  const selectedLegalArea = useState<{ value: string, label: string }[]>('author-filter-legal-area', () => [])

  const searchLegalArea = ref<string>('')

  const solrParams = computed<AuthorParams>(() => ({
    [SolrParamsKey.Authors]: {
      nodeType: 'author',
      sort: 'surname ASC',
      filter: 'bodytext:[* TO *]',
    },
  }))

  const { data: authorsData, isLoading, suspense } = useAuthorQuery(solrParams)

  const legalAreaOptions = computed(() => {
    const docs = authorsData.value?.response.docs ?? []
    const allLegalAreas = new Set<string>()

    for (const doc of docs) {
      for (const area of doc.legalAreas) {
        allLegalAreas.add(area)
      }
    }

    return Array.from(allLegalAreas)
      .map(area => ({ value: area, label: extractLabel(area) }))
      .sort((a, b) => compareLocaleNorwegian(a.label, b.label))
  })

  const allAuthors = computed(() => authorsData.value?.response.docs ?? [])

  const committedState = computed<AuthorFilterState>(() => ({
    searchKeyword: searchKeyword.value,
    selectedLegalArea: selectedLegalArea.value,
  }))

  const filteredAuthors = computed(() => {
    if (!allAuthors.value.length) {
      return []
    }
    const state = committedState.value
    return allAuthors.value.filter(author => authorMatches(author, state))
  })

  const countForFacet = createCountWithFilters(allAuthors, committedState, authorMatches)

  function countWithFilters(overrides: AuthorDrawerOverrides): number {
    return countForFacet(overrides)
  }

  const { selected: legalAreaSelected, toggle: toggleLegalArea, facetOptions: legalAreaFacetOptions } = useFacetFilter(selectedLegalArea, legalAreaOptions, option => countForFacet({ selectedLegalArea: [option] }))

  const sortedFilteredAuthors = computed(() => {
    return [...filteredAuthors.value].sort((a, b) => {
      const surnameA = normalizeNorwegianSort(a.surname || getLastName(a.name))
      const surnameB = normalizeNorwegianSort(b.surname || getLastName(b.name))
      return compareLocaleNorwegian(surnameA, surnameB)
    })
  })

  const paginatedAuthors = computed(() => {
    const startIndex = (page.value - 1) * AUTHORS_PER_PAGE
    const endIndex = startIndex + AUTHORS_PER_PAGE
    return sortedFilteredAuthors.value.slice(startIndex, endIndex)
  })

  const numFound = computed(() => filteredAuthors.value.length)

  watch([searchKeyword, selectedLegalArea], () => (page.value = 1))

  return {
    page,
    searchKeyword,
    selectedLegalArea,
    legalAreaOptions,
    isLoading,
    suspense,
    authors: paginatedAuthors,
    numFound,
    setPage: (newPage: number) => (page.value = newPage),
    countWithFilters,
    legalAreaSelected,
    toggleLegalArea,
    legalAreaFacetOptions,
    searchLegalArea,
  }
}

export const useAuthorFilters = createSharedComposable(_useAuthorFilters)
